import React from 'react';
import { Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { LinearProgress } from '@mui/material';

import { useCustomerConfig, useCustomerConfigLoaded, usePortalProductsState, useProductsFailed, useProductsLoaded, useUser, useRooftop } from '@store';
import { isProductRouteMounted } from '@features/products/productGates';

import ProtectedRoute from '@components/ProtectedRoute';
import { FeatureName } from '@utils/featureNames';

// Dev-only sandbox; lazy so it is never part of the main chunk (and the whole
// route is compiled out of a build by the import.meta.env.DEV guard below).
const DriverPickerSandboxPage = React.lazy(
  () => import('@features/devSandbox/pages/DriverPickerSandboxPage')
);

import AppointmentDetailsPage from '@features/appointments/pages/AppointmentDetails';
import AppointmentsPage from '@features/appointments/pages/Appointments';
import AppraisalDetailsPage from '@features/appraisals/pages/AppraisalDetailsPage';
import AppraisalIndexTab from '@features/appraisals/tabs/AppraisalIndexTab';
import HomeLayout from '@features/home/HomeLayout';
import BillingPage from '@features/billing/pages/BillingPage';
import DriversPage from '@features/drivers/pages/DriversPage';
import InvoicesPage from '@features/invoices/pages/InvoicesPage';
import ProgramsPage from '@features/programs/pages/ProgramsPage';
import RideSharesTab from '@features/ridehails/tabs/RideSharesTab';
import RidehailDetailsPage from '@features/ridehails/pages/RidehailDetailsPage';
import { ValetOrdersTab, ValetOrdersTabErrorBoundary } from '@hopdrive/feature-order-index';
import DealerOrderIndexAdapter from '@features/orderIndex/DealerOrderIndexAdapter';
import SchedulePage from '@features/schedule/pages/SchedulePage';
import LocationDetailPage from '@features/locations/pages/LocationDetailPage';
import LocationsPage from '@features/locations/pages/LocationsPage';
import LoginPage from '@features/auth/pages/LoginPage';
import SetPasswordPage from '@features/auth/pages/SetPasswordPage';
import MissingRefReportPage from '@features/missingRefReport/pages/MissingRefReportPage';
import MissingRefReportTab from '@features/missingRefReport/MissingRefReportTab';
import MoveDetailsPage from '@features/moves/pages/MoveDetailsPage';
import MoveImportPage from '@features/moveImport/pages/MoveImportPage';
import MovePlannerPage from '@features/moveCreation/pages/MovePlannerPage';
import ViewAssignmentsPage from '@features/viewAssignments/pages/ViewAssignmentsPage';
import TeamTab from '@features/moves/tabs/TeamTab';
import OrganizationOverview from '@features/rooftops/pages/OrganizationOverview';
import ProfilePage from '@features/auth/pages/ProfilePage';
import ReportsTab from '@features/reporting/tabs/ReportsTab';
import SupportPage from '@features/support/SupportPage';
import LearningCenterPage from '@features/learningCenter/pages/LearningCenterPage';
import LearningCenterModulePage from '@features/learningCenter/pages/LearningCenterModulePage';
import { ReportingConfig } from '@features/reporting/types';
import UserAgreementPage from '@features/auth/pages/UserAgreementPage';
import SearchResultsPage from '@features/searchResults/pages/SearchResultsPage';
import { GlobalSearchPage } from '@features/globalSearch';
import SettingsPage from '@features/settings/pages/SettingsPage';
import AddTeamMember from '@features/userManagement/pages/AddTeamMember';
import UserDetailsPage from '@features/userManagement/pages/UserDetails';
import TeamDashboardPage from '@features/userManagement/pages/TeamDashboardPage';
import RidesTab from '@features/guestRides/tabs/RidesTab';
import GuestRidesShellLayout from '@features/guestRides/GuestRidesShellLayout';
import GuestRideApprovalPage from '@features/guestRides/approval/GuestRideApprovalPage';
import MSRunDetailPage from '@features/mobileService/pages/RunDetailPage';
import MSAppointmentsTab from '@features/mobileService/tabs/AppointmentsTab';
import MSRunsTab from '@features/mobileService/tabs/RunsTab';
import MSRunsMapTab from '@features/mobileService/tabs/RunsMapTab';
import { resolveMobileServiceActions } from '@features/mobileService/resolveMobileServiceActions';
import PDRunDetailPage from '@features/partsDelivery/pages/RunDetailPage';
import PartsDeliveryShellLayout from '@features/partsDelivery/PartsDeliveryShellLayout';
import PDPendingOrdersTab from '@features/partsDelivery/tabs/PendingOrdersTab';
import PDActiveRunsTab from '@features/partsDelivery/tabs/ActiveRunsTab';
import PDTemplatesTab from '@features/partsDelivery/tabs/TemplatesTab';
import PDReturnsTab from '@features/partsDelivery/tabs/ReturnsTab';
import {
  hasValetTab,
  isPortalAccessSettled,
  mobileServiceGatedTabs,
  resolveAppraisalsTabs,
  resolveMobileServiceTabs,
  resolveValetActions,
  resolveValetTabs,
  unmountedTabs,
  valetGatedTabs,
} from '@hopdrive/feature-portal-products';
import ProductShellLayout from '@features/products/shell/ProductShellLayout';
import ProductSettingsRoute from '@features/products/shell/ProductSettingsRoute';
import LegacyRedirect from '@features/products/shell/LegacyRedirect';
import {
  isMoveDetailSegment,
} from '@features/products/shell/tabRoutes';
import MobilityVehiclesPage from '@features/mobilityVehicles/pages/MobilityVehiclesPage';
import ServiceVehiclesTab from '@features/mobilityVehicles/ServiceVehiclesTab';
import { LEGACY_PRODUCT_REDIRECTS, PRODUCT_ROUTES, importMovesPath, movePath, movesTabPath, planMovesPath, productRootPath, ridesharePath } from '@features/products/productRoutes';

// Resolved once so the layout's memoized tab list keeps a stable identity. Mobile Service's tabs
// depend on config (the Missing Ref Report), so they resolve inside the component instead.
const MOBILE_SERVICE_ACTIONS = resolveMobileServiceActions();
const APPRAISALS_TABS = resolveAppraisalsTabs(PRODUCT_ROUTES);

/** The `/moves/:id` detail route: renders the move details for a numeric id, and sends any other
 * segment to the Orders tab, so the details page never mounts on a bad id. Every Moves tab's path is
 * a static route (the tab when the customer has it, its restricted page when not), so only a
 * mistyped or unknown word reaches here and lands on Orders. */
const MoveDetailRoute: React.FC = () => {
  const { id } = useParams();
  if (!isMoveDetailSegment(id)) return <Navigate to={productRootPath('moves')} replace />;
  return <MoveDetailsPage />;
};

const AppRoutes: React.FC = () => {
  const rooftop = useRooftop();
  const customerConfig = useCustomerConfig();
  const showMissingRefReport = customerConfig?.reference_num_report_enabled || false;
  // The Missing Ref tab uses the same config gate and label as its nav entry.
  const missingRefLabel = customerConfig?.reference_num_report_label || 'Missing Ref Report';
  const missingRefTabOptions = React.useMemo(
    () => ({ missingRefLabel: showMissingRefReport ? missingRefLabel : undefined }),
    [showMissingRefReport, missingRefLabel],
  );
  const isCSVImportEnabled = customerConfig?.enable_csv_import || false;
  const reportingDashboardEnabled = (customerConfig?.reporting as ReportingConfig | undefined)?.enabled === true;
  // Each product's pages are gated on the portal products, the same model the sidebar reads. A
  // product's shell route itself is always registered: the layout renders the restricted fallback
  // for a hidden product.
  const products = usePortalProductsState();
  const movesRouteMounted = isProductRouteMounted(products?.entries.moves);
  const mobileServiceRouteMounted = isProductRouteMounted(products?.entries['mobile-service']);
  const partsDeliveryRouteMounted = isProductRouteMounted(products?.entries['parts-delivery']);
  const appraisalsRouteMounted = isProductRouteMounted(products?.entries.appraisals);
  // Moves' gated tabs decide which of its tab routes mount; memoized so the layout's tab list is stable.
  const valetEntry = products?.entries.moves;
  const valetTabs = React.useMemo(
    () => resolveValetTabs(PRODUCT_ROUTES, valetEntry, { ...missingRefTabOptions, reportingEnabled: reportingDashboardEnabled }),
    [valetEntry, missingRefTabOptions, reportingDashboardEnabled],
  );
  const mobileServiceTabs = React.useMemo(() => resolveMobileServiceTabs(PRODUCT_ROUTES, missingRefTabOptions), [missingRefTabOptions]);
  // The gated tabs this customer lacks (`unmountedTabs` returns only absent ones); each renders
  // RESTRICTED ACCESS at its own path, beside the shell.
  const restrictedValetTabs = React.useMemo(() => unmountedTabs(valetGatedTabs(PRODUCT_ROUTES), valetTabs), [valetTabs]);
  const restrictedMobileServiceTabs = React.useMemo(
    () => unmountedTabs(mobileServiceGatedTabs(PRODUCT_ROUTES), mobileServiceTabs),
    [mobileServiceTabs],
  );
  // Valet's header actions: Plan Moves, plus Import Moves when the rooftop has the CSV import.
  const valetActions = React.useMemo(() => resolveValetActions(PRODUCT_ROUTES, { csvImportEnabled: isCSVImportEnabled }), [isCSVImportEnabled]);

  // Extract appointment management config
  const appointmentManagementEnabled = React.useMemo(() => {
    if (!rooftop?.config) return false;

    try {
      const config = typeof rooftop.config === 'string' ? JSON.parse(rooftop.config) : rooftop.config;
      return config?.appointment_management?.enabled === true;
    } catch (e) {
      console.error('Error parsing rooftop config for appointment_management:', e);
      return false;
    }
  }, [rooftop?.config]);

  return (
    <Routes>
      {/* Dev-only component sandboxes. `import.meta.env.DEV` is statically false in
          a build, so these routes (and the lazy chunk behind them) are dropped
          from any shipped bundle. Unauthenticated on purpose: they render
          components against fixtures and talk to no backend. */}
      {import.meta.env.DEV && (
        <Route
          path='/dev/driver-picker'
          element={
            <React.Suspense fallback={<LinearProgress />}>
              <DriverPickerSandboxPage />
            </React.Suspense>
          }
        />
      )}

      <Route path='/' element={<LoggedInToHome />} />
      <Route path='/login' element={<LoggedInToHome />} />
      <Route path='/set-password' element={<SetPasswordPage />} />
      <Route
        path='/profile'
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/user-agreement'
        element={
          <ProtectedRoute>
            <UserAgreementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/invoices'
        element={
          <ProtectedRoute requiredFeature='invoices'>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/programs'
        element={
          <ProtectedRoute>
            <ProgramsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/invoices/:id'
        element={
          <ProtectedRoute requiredFeature='invoices'>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      {/* Every route outside this product block is a platform route. */}
      {/* Home's tabs share one page and read the tab from the location. */}
      <Route
        path='/home'
        element={
          <ProtectedRoute>
            <HomeLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path={productRootPath('moves')}
        element={
          <ProtectedRoute>
            <ProductShellLayout product='moves' tabs={valetTabs} actions={valetActions} />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <DealerOrderIndexAdapter>
              <ValetOrdersTabErrorBoundary>
                <ValetOrdersTab />
              </ValetOrdersTabErrorBoundary>
            </DealerOrderIndexAdapter>
          }
        />
        {hasValetTab(valetTabs, 'team') ? <Route path='team' element={<TeamTab />} /> : null}
        {hasValetTab(valetTabs, 'schedule') ? <Route path='schedule' element={<SchedulePage />} /> : null}
        {hasValetTab(valetTabs, 'rideshares') ? <Route path='rideshares' element={<RideSharesTab />} /> : null}
        {hasValetTab(valetTabs, 'reports') ? <Route path='reports' element={<ReportsTab />} /> : null}
        {hasValetTab(valetTabs, 'missing-ref') ? <Route path='missing-ref' element={<MissingRefReportTab lockedView='moves' />} /> : null}
      </Route>
      {/* A gated Moves tab this customer lacks renders RESTRICTED ACCESS at its own path. */}
      {restrictedValetTabs.map(tab => (
        <Route key={tab.path} path={tab.path} element={<ProtectedRoute available={false} />} />
      ))}
      {/* Moves' pages beside the shell: the details, the rideshare detail, the planner, and the CSV
          import. Detail sits at `/moves/:id` beside the tab paths; the guard sends a non-numeric
          segment that is no known page to the Orders tab. */}
      <Route
        path={movePath(':id')}
        element={
          <ProtectedRoute available={movesRouteMounted}>
            <MoveDetailRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path={ridesharePath(':moveId')}
        element={
          <ProtectedRoute available={hasValetTab(valetTabs, 'rideshares')}>
            <RidehailDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={planMovesPath()}
        element={
          <ProtectedRoute requiredFeature={FeatureName.PLANNING} available={movesRouteMounted}>
            <MovePlannerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={importMovesPath()}
        element={
          <ProtectedRoute requiredFeature={FeatureName.PLANNING} available={movesRouteMounted && isCSVImportEnabled}>
            <MoveImportPage />
          </ProtectedRoute>
        }
      />
      {/* The legacy product paths land on their product-prefixed replacements, params and query kept. */}
      {LEGACY_PRODUCT_REDIRECTS.map(redirect => (
        <Route key={redirect.from} path={redirect.from} element={<LegacyRedirect to={redirect.to} />} />
      ))}
      <Route
        path='/appraisals'
        element={
          <ProtectedRoute requiredFeature='appraisals'>
            <ProductShellLayout product='appraisals' tabs={APPRAISALS_TABS} />
          </ProtectedRoute>
        }
      >
        <Route index element={<AppraisalIndexTab />} />
        <Route path='settings' element={<ProductSettingsRoute product='appraisals' />} />
      </Route>
      <Route
        path='/appraisals/:id'
        element={
          <ProtectedRoute requiredFeature='appraisals' available={appraisalsRouteMounted}>
            <AppraisalDetailsPage />
          </ProtectedRoute>
        }
      />
      {/* The approver's landing page from the approval text or email. Outside the shell's tabs so
          it fills a phone screen. */}
      <Route
        path='/guest-rides/:id/approval'
        element={
          <ProtectedRoute>
            <GuestRideApprovalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/guest-rides'
        element={
          <ProtectedRoute>
            <GuestRidesShellLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RidesTab />} />
        <Route path='settings' element={<ProductSettingsRoute product='guest-rides' />} />
      </Route>
      <Route
        path='/mobile-service'
        element={
          <ProtectedRoute requiredFeature={FeatureName.MOBILE_SERVICE}>
            <ProductShellLayout product='mobile-service' tabs={mobileServiceTabs} actions={MOBILE_SERVICE_ACTIONS} />
          </ProtectedRoute>
        }
      >
        <Route index element={<MSAppointmentsTab />} />
        <Route path='runs' element={<MSRunsTab />} />
        <Route path='map' element={<MSRunsMapTab />} />
        <Route path='service-vehicles' element={<ServiceVehiclesTab />} />
        {showMissingRefReport ? <Route path='missing-ref' element={<MissingRefReportTab lockedView='mobileservice' />} /> : null}
      </Route>
      {restrictedMobileServiceTabs.map(tab => (
        <Route
          key={tab.path}
          path={tab.path}
          element={<ProtectedRoute available={false} />}
        />
      ))}
      {/* Only a tab's main content renders inside a product shell; details, forms, and flows are
          full pages beside it, kept under the product prefix so the nav entry stays lit. */}
      <Route
        path='/mobile-service/runs/:id'
        element={
          <ProtectedRoute requiredFeature={FeatureName.MOBILE_SERVICE} available={mobileServiceRouteMounted}>
            <MSRunDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/parts-delivery'
        element={
          <ProtectedRoute requiredFeature={FeatureName.PARTS_DELIVERY}>
            <PartsDeliveryShellLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PDPendingOrdersTab />} />
        <Route path='live' element={<PDActiveRunsTab />} />
        <Route path='templates' element={<PDTemplatesTab />} />
        <Route path='returns' element={<PDReturnsTab />} />
        <Route path='service-vehicles' element={<ServiceVehiclesTab />} />
      </Route>
      <Route
        path='/parts-delivery/runs/:id'
        element={
          <ProtectedRoute requiredFeature={FeatureName.PARTS_DELIVERY} available={partsDeliveryRouteMounted}>
            <PDRunDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/search-moves'
        element={
          <ProtectedRoute>
            <SearchResultsPage />
          </ProtectedRoute>
        }
      />
      {/* Global cross-entity search (header search bar lands here).
          Auth-only: row visibility is enforced by Hasura role permissions. */}
      <Route
        path='/search'
        element={
          <ProtectedRoute>
            <GlobalSearchPage />
          </ProtectedRoute>
        }
      />
      {/* Lite only: the offer report's whole subject is the offer lifecycle, which a Full
          Service rooftop never enters — HopDrive dispatches its work. */}
      <Route
        path='/view-assignments'
        element={
          <ProtectedRoute requireLiteTier>
            <ViewAssignmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/locations'
        element={
          <ProtectedRoute>
            <LocationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/locations/:id'
        element={
          <ProtectedRoute>
            <LocationDetailPage />
          </ProtectedRoute>
        }
      />
      {/* Reports is a Moves tab; the old standalone path redirects there, and the tab's path renders
          RESTRICTED ACCESS when reporting is disabled. */}
      <Route path='/reports' element={<LegacyRedirect to={movesTabPath('reports')} />} />
      <Route
        path='/support'
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/learning-center'
        element={
          <ProtectedRoute>
            <LearningCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/learning-center/:key'
        element={
          <ProtectedRoute>
            <LearningCenterModulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/organization-view'
        element={
          <ProtectedRoute>
            <OrganizationOverview />
          </ProtectedRoute>
        }
      />
      <Route
        path='/missing-ref'
        element={
          <ProtectedRoute available={showMissingRefReport}>
            <MissingRefReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/billing-preferences'
        element={
          <ProtectedRoute requiredFeature='invoices'>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users'
        element={
          <ProtectedRoute requiredFeature='user-management' blockAdmin>
            <TeamDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users/add'
        element={
          <ProtectedRoute requiredFeature={['user-management', 'driver-management']} blockAdmin>
            <AddTeamMember />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users/:id'
        element={
          <ProtectedRoute requiredFeature='user-management' blockAdmin>
            <UserDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/appointments'
        element={
          <ProtectedRoute>
            <AppointmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/appointments/:id'
        element={
          <ProtectedRoute>
            <AppointmentDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/appointments/add'
        element={
          <ProtectedRoute>
            <AppointmentDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/drivers'
        element={
          <ProtectedRoute requiredFeature='driver-management' blockAdmin>
            <DriversPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/drivers/:id'
        element={
          <ProtectedRoute requiredFeature='driver-management' blockAdmin>
            <UserDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/settings'
        element={
          <ProtectedRoute available={appointmentManagementEnabled}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/service-vehicles'
        element={
          <ProtectedRoute
            requiredFeature={[FeatureName.MOBILE_SERVICE, FeatureName.PARTS_DELIVERY]}
            available={mobileServiceRouteMounted || partsDeliveryRouteMounted}
          >
            <MobilityVehiclesPage />
          </ProtectedRoute>
        }
      />
      <Route path='*' element={<NoMatch />}></Route>
    </Routes>
  );
};

function LoggedInToHome() {
  const user = useUser();

  // A logged-in user lands on Home (the terms modal in App.tsx handles acceptance).
  if (user) return <Navigate to='/home' replace />;

  return <LoginPage />;
}

function NoMatch() {
  const location = useLocation();
  const user = useUser();
  const customerConfigLoaded = useCustomerConfigLoaded();
  const productsLoaded = useProductsLoaded();
  const products = usePortalProductsState();
  const productsFailed = useProductsFailed();

  // Hold a bad URL's 404 until the rooftop's config and products settle, with the same top-of-page
  // loading bar the gated pages show, so a cold load never paints "No match" before the route table
  // settles. Once both have settled (or when logged out), 404.
  const settled = isPortalAccessSettled({
    hostReady: customerConfigLoaded,
    productsLoaded,
    productsPresent: products !== undefined,
    productsFailed,
  });
  if (user && !settled) {
    return <LinearProgress />;
  }

  return (
    <div style={{ marginTop: 200 }}>
      <h3>
        No match for <code>{location.pathname}</code>
      </h3>
    </div>
  );
}

export default AppRoutes;
